<script setup lang="ts">
import { computed, ref } from 'vue';

import ChevronIcon from '@/assets/svg/conversations/ChevronIcon.svg?component';
import MessageTemplate from '@/components/Shared/Input/Template/MessageTemplate.vue';
import { UnitedStatesStates } from '@/core/jobs/copilot-activation/utils/state.utils';
import { GeolocationPersistence } from '@/core/shared/geolocation/geolocation.persistence';
import { isRequired } from '@/utils/forms';
import { useSuggestedStates } from './useSuggestedStates';

const props = defineProps<{
  modelValue: string | null;
  city: string | null;
  suggestionsEnabled: boolean;
  disabled: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', state: string | null): void;
}>();

const { suggestedStates, stateItems } = useSuggestedStates(
  () => props.city,
  new GeolocationPersistence(),
  () => props.suggestionsEnabled,
);

// The group headers only make sense over the full list. Typing filters it, so they hide and the
// order alone keeps the suggestions first.
const search = ref('');
const selectedName = computed(
  () => UnitedStatesStates.find((state) => state.abbreviation === props.modelValue)?.name ?? '',
);
const showsGroups = computed(() => {
  const typed = search.value.trim();
  return suggestedStates.value.length > 0 && (!typed || typed === selectedName.value);
});
</script>

<template>
  <v-autocomplete
    :model-value="modelValue"
    data-test="job-state"
    :items="stateItems"
    item-title="name"
    item-value="abbreviation"
    variant="outlined"
    density="compact"
    placeholder="Select state"
    :menu-icon="ChevronIcon"
    :return-object="false"
    :menu-props="{ maxWidth: '536px' }"
    :rules="[isRequired]"
    :disabled="disabled"
    validate-on="submit"
    @update:model-value="emit('update:modelValue', $event)"
    @update:search="search = $event ?? ''"
  >
    <template v-if="showsGroups" #prepend-item>
      <v-list-subheader data-test="suggested-states-header">Suggested states</v-list-subheader>
    </template>
    <template #item="{ index, props: itemProps }">
      <template v-if="showsGroups && index === suggestedStates.length">
        <v-divider class="my-1" />
        <v-list-subheader data-test="all-states-header">All states</v-list-subheader>
      </template>
      <v-list-item v-bind="itemProps" />
    </template>
    <template #message="{ message }">
      <MessageTemplate :message="message" />
    </template>
  </v-autocomplete>
</template>
